import React, { useEffect, useRef, useCallback } from 'react'
import { isOfType, upperFirst } from 'global'
import { SliderTrack } from './SliderTrack'
import { SliderRail } from './SliderRail'
import { SliderHandle, HandleContentWrapper } from './SliderHandle'
import { SliderThumb } from './SliderThumb'

interface ISlider2Props {
  vertical?: boolean
  max?: number
  // Set the percentage of the slider
  percentage?: number
  // positive or negative number to shift handle start location
  startOffset?: number
  // positive or negative number to shift handle end location
  endOffset?: number
  // positive or negative number to shift the rail forward or
  // backwards from the center of the handle by an amount in pixels.
  railOffset?: number
  trackWidth?: string
  trackHeight?: string
  onChange?: (result: number) => void
}

type DivRef = React.RefObject<HTMLDivElement>

// Calculate slider internal percent (how far to move the rail and thumb)
const getPercentage = (current: number, max: number, invert = false) =>
  invert ? 100 - (100 * current) / max : (100 * current) / max

// Get value for output
const getValue = (percentage: number, max: number) => {
  const val = (max / 100) * percentage
  return Math.round(Math.min(Math.max(val, 0), max))
}

// Calculate thumb position from percentage
const getThumbPosition = (percentage: number, invert = false) =>
  `${invert ? 100 - percentage : percentage}%`

const getRailPosition = (percentage: number, offset: number) =>
  `calc(${percentage}% + ${offset}px)`

const getTrackEnd = (trackRef: DivRef, thumbRef: DivRef, direction: string) =>
  trackRef && trackRef.current && thumbRef && thumbRef.current
    ? trackRef.current[`offset${upperFirst(direction)}`] -
      thumbRef.current[`offset${upperFirst(direction)}`]
    : 0

const getTrackRange = (
  trackRef: DivRef,
  thumbRef: DivRef,
  direction: string
) => [0, getTrackEnd(trackRef, thumbRef, direction)]

const eventClientCoord = (
  event: MouseEvent | React.MouseEvent | TouchEvent | React.TouchEvent,
  axis: 'Y' | 'X' = 'X'
) => {
  let clientCoord: number
  if (
    window.TouchEvent &&
    (event instanceof TouchEvent || isOfType<TouchEvent>(event, 'touches'))
  ) {
    clientCoord = (event as TouchEvent).touches[0][`client${axis}`]
  } else {
    clientCoord = event[`client${axis}`]
  }
  return clientCoord
}

export const Slider = ({
  vertical = false,
  percentage = 0,
  max = 100,
  startOffset = 0,
  endOffset = 0,
  railOffset = 0,
  trackWidth,
  trackHeight,
  onChange,
}: ISlider2Props) => {
  const trackRef = useRef<HTMLDivElement | null>(null)
  const railRef = useRef<HTMLDivElement | null>(null)
  const thumbRef = useRef<HTMLDivElement | null>(null)
  const diff = useRef<number | null>(null)

  const initialPercentage = getPercentage(percentage, max)
  const rectPositionProp = vertical ? 'top' : 'left'
  const axis = vertical ? 'Y' : 'X'
  const trackDirection = vertical ? 'height' : 'width'

  const handleMouseMove = (event: MouseEvent | TouchEvent) => {
    if (
      !diff?.current ||
      !trackRef?.current ||
      !railRef?.current ||
      !thumbRef?.current
    ) {
      return
    }
    const newAxisCoord =
      eventClientCoord(event, axis) -
      diff.current -
      trackRef.current.getBoundingClientRect()[rectPositionProp]

    const [newPercentage, newValue] = calc(newAxisCoord)

    // Set the thumb position without rerendering
    thumbRef.current.style[rectPositionProp] = getThumbPosition(
      newPercentage,
      vertical
    )

    // Set the rail position without rerendering
    railRef.current.style[trackDirection] = getRailPosition(
      newPercentage,
      railOffset
    )

    if (onChange) {
      onChange(newValue)
    }
  }

  const handleSliderEnd = () => {
    document.removeEventListener('mouseup', handleSliderEnd)
    document.removeEventListener('mousemove', handleMouseMove)

    if (window.TouchEvent) {
      document.removeEventListener('touchmove', handleMouseMove)
      document.removeEventListener('touchend', handleSliderEnd)
    }
  }

  const handleSlideStart = (event: React.MouseEvent | React.TouchEvent) => {
    if (thumbRef.current) {
      diff.current =
        eventClientCoord(event, axis) -
        thumbRef.current.getBoundingClientRect()[rectPositionProp]

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleSliderEnd)

      if (window.TouchEvent) {
        document.addEventListener('touchmove', handleMouseMove)
        document.addEventListener('touchend', handleSliderEnd)
      }
    }
  }

  const calc = useCallback(
    (newAxisCoord: number) => {
      const [start, end] = getTrackRange(trackRef, thumbRef, trackDirection)

      // Create a copy of the coord to seperate the value that can be offset, and the
      // value that is output.
      let newOffsetAxisCoord = newAxisCoord

      if (newAxisCoord < start + startOffset) {
        newOffsetAxisCoord = start + startOffset
      }

      if (newAxisCoord > end + endOffset) {
        newOffsetAxisCoord = end + endOffset
      }

      const newPercentage = getPercentage(newOffsetAxisCoord, end, vertical)
      const newValue = getValue(getPercentage(newAxisCoord, end, vertical), max)
      return [newPercentage, newValue]
    },
    [trackRef, thumbRef, trackDirection, endOffset, startOffset, max, vertical]
  )

  // Set initial positions and apply thumb & rail positioning offsets
  useEffect(() => {
    if (thumbRef.current && railRef.current) {
      const [, end] = getTrackRange(trackRef, thumbRef, trackDirection)

      // Get coords from percentage (invert percentage for vertical mode)
      const newAxisCoord = getValue(
        vertical ? 100 - initialPercentage : initialPercentage,
        end
      )

      const [newPercentage] = calc(newAxisCoord)

      // Reset thumb positions applied in previously set modes.
      if (vertical) {
        thumbRef.current.style.left = '0'
        railRef.current.style.width = 'auto'
      } else {
        thumbRef.current.style.top = '0'
        railRef.current.style.height = 'auto'
      }

      thumbRef.current.style[rectPositionProp] = getThumbPosition(
        newPercentage,
        vertical
      )
      railRef.current.style[trackDirection] = getRailPosition(
        newPercentage,
        railOffset
      )
    }
  }, [
    thumbRef,
    rectPositionProp,
    initialPercentage,
    vertical,
    endOffset,
    railOffset,
    startOffset,
    trackDirection,
    calc,
  ])

  return (
    <>
      <SliderTrack
        // prevent double tap zoom (note touch-action: none | manipulate; is not being respected)
        onClick={(e) => e.preventDefault()}
        vertical={vertical}
        ref={trackRef}
        width={trackWidth}
        height={trackHeight}
      >
        <SliderHandle
          ref={thumbRef}
          vertical={vertical}
          onMouseDown={handleSlideStart}
          onTouchStart={handleSlideStart}
        >
          <HandleContentWrapper vertical={vertical}>
            <SliderThumb vertical={vertical} />
          </HandleContentWrapper>
        </SliderHandle>
        <SliderRail vertical={vertical} ref={railRef} />
      </SliderTrack>
    </>
  )
}
